import React, { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Truck,
  Search,
  Filter,
  Calendar,
  Eye,
  PlusCircle,
  Clock,
  CheckCircle2,
  AlertCircle,
  MapPin,
  RefreshCw
} from 'lucide-react';
import { useAreaCliente } from '../../contexts/AreaClienteContext';
import { areaClienteService } from '../../services/areaClienteService';
import { OrdemDeServico } from '../../types/ordemServico';
import { AreaClienteDetalhesOSModal } from './AreaClienteDetalhesOSModal';

export function AreaClienteOrdensServico() {
  const { clienteAtual } = useAreaCliente();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ordens, setOrdens] = useState<OrdemDeServico[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedOS, setSelectedOS] = useState<OrdemDeServico | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('todos');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const loadOS = async () => {
    if (!clienteAtual) return;
    setIsLoading(true);
    try {
      const data = await areaClienteService.getOrdensServicoCliente(clienteAtual.id);
      setOrdens(data);
    } catch (err) {
      console.error('Erro ao carregar ordens de serviço:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadOS();
  }, [clienteAtual]);

  // Suporta abrir uma OS específica direto por link (ex: enviado por
  // notificação de WhatsApp: /area-cliente/os?os=123).
  useEffect(() => {
    const osIdParam = searchParams.get('os');
    if (!osIdParam || ordens.length === 0) return;
    const encontrada = ordens.find((o) => String(o.id) === osIdParam);
    if (encontrada) {
      setSelectedOS(encontrada);
    }
  }, [searchParams, ordens]);

  // Filter logic
  const filteredOrdens = useMemo(() => {
    return ordens.filter(os => {
      // Status filter
      if (statusFilter === 'aberto' && os.status_id !== 8) return false;
      if (statusFilter === 'agendado' && os.status_id !== 1) return false;
      if (statusFilter === 'andamento' && os.status_id !== 2 && os.status_id !== 6) return false;
      if (statusFilter === 'finalizado' && os.status_id !== 4 && os.status_id !== 7 && os.status_id !== 9) return false;
      if (statusFilter === 'cancelado' && os.status_id !== 3) return false;

      // Search term
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        const matchId = String(os.id).includes(term);
        const matchDesc = (os.descricao || '').toLowerCase().includes(term);
        const matchRetirada = (os.retirada || '').toLowerCase().includes(term);
        const matchEntrega = (os.entrega || '').toLowerCase().includes(term);
        const matchVeiculo = (os.veiculos_id || '').toLowerCase().includes(term);
        const matchMotorista = String(os.agregado || '').toLowerCase().includes(term);

        if (!matchId && !matchDesc && !matchRetirada && !matchEntrega && !matchVeiculo && !matchMotorista) {
          return false;
        }
      }

      // Date range filter
      if (startDate && os.data_atendimento && os.data_atendimento < startDate) return false;
      if (endDate && os.data_atendimento && os.data_atendimento > endDate) return false;

      return true;
    });
  }, [ordens, statusFilter, searchTerm, startDate, endDate]);

  const getStatusBadge = (statusId?: number) => {
    switch (statusId) {
      case 8:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-300 flex items-center gap-1"><Clock className="w-3 h-3" /> Solicitado / Em Aberto</span>;
      case 1:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-blue-100 text-blue-800 border border-blue-300">Agendado</span>;
      case 2:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-orange-100 text-orange-800 border border-orange-300">Em Andamento</span>;
      case 4:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Finalizado</span>;
      case 7:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-green-100 text-green-900 border border-green-300">Medição Gerada</span>;
      case 9:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-purple-100 text-purple-900 border border-purple-300">Faturado</span>;
      case 3:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-red-100 text-red-800 border border-red-300">Cancelado</span>;
      case 6:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-yellow-100 text-yellow-800 border border-yellow-300">Aguardando Assinatura</span>;
      default:
        return <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">Status #{statusId}</span>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Title & Action */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
        <div>
          <div className="flex items-center space-x-2">
            <div className="p-2 bg-red-50 text-red-600 rounded-xl">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-slate-900">Ordens de Serviço</h1>
              <p className="text-xs text-slate-500">
                Acompanhe o status e histórico de todas as viagens e solicitações da sua empresa.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={loadOS}
            className="p-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl transition-colors text-xs font-semibold flex items-center gap-1.5"
            title="Atualizar lista"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </button>
          <Link
            to="/area-cliente/solicitar-frete"
            className="inline-flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl shadow-md shadow-red-600/30 transition-all hover:scale-105 active:scale-95"
          >
            <PlusCircle className="w-4 h-4" />
            <span>Solicitar Novo Frete</span>
          </Link>
        </div>
      </div>

      {/* Filter Tabs & Search Bar */}
      <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-sm space-y-4">
        {/* Status Pills */}
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'todos', label: 'Todas as OS', count: ordens.length },
            { id: 'aberto', label: 'Solicitadas (Em Aberto)', count: ordens.filter(o => o.status_id === 8).length },
            { id: 'agendado', label: 'Agendadas', count: ordens.filter(o => o.status_id === 1).length },
            { id: 'andamento', label: 'Em Andamento', count: ordens.filter(o => o.status_id === 2 || o.status_id === 6).length },
            { id: 'finalizado', label: 'Finalizadas / Medidas / Faturadas', count: ordens.filter(o => o.status_id === 4 || o.status_id === 7 || o.status_id === 9).length },
            { id: 'cancelado', label: 'Canceladas', count: ordens.filter(o => o.status_id === 3).length },
          ].map(tab => {
            const isActive = statusFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setStatusFilter(tab.id)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-semibold transition-all flex items-center space-x-1.5 ${
                  isActive
                    ? 'bg-slate-900 text-white shadow-sm'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.2 rounded-full text-[10px] ${
                  isActive ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 gap-3 pt-2">
          <div className="sm:col-span-2 relative">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por Nº OS, endereço, carga ou placa..."
              className="w-full pl-9 pr-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
            />
          </div>

          <div>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
              title="Data inicial"
            />
          </div>

          <div>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 text-xs bg-slate-50 border border-slate-200 rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-red-500"
              title="Data final"
            />
          </div>
        </div>
      </div>

      {/* OS List */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="py-16 text-center">
            <div className="w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-3 text-xs text-slate-500">Carregando ordens de serviço...</p>
          </div>
        ) : filteredOrdens.length === 0 ? (
          <div className="py-16 text-center space-y-3 px-4">
            <Truck className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="text-sm font-bold text-slate-800">Nenhuma Ordem de Serviço encontrada</h3>
            <p className="text-xs text-slate-500 max-w-sm mx-auto">
              Nenhuma viagem corresponde aos filtros selecionados. Tente ajustar o status ou período.
            </p>
            <button
              onClick={() => {
                setStatusFilter('todos');
                setSearchTerm('');
                setStartDate('');
                setEndDate('');
              }}
              className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold"
            >
              Limpar Filtros
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-bold uppercase tracking-wider text-[11px]">
                <tr>
                  <th className="py-3 px-4">Nº OS</th>
                  <th className="py-3 px-4">Data & Horário</th>
                  <th className="py-3 px-4">Descrição da Carga</th>
                  <th className="py-3 px-4">Origem / Destino</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-center">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredOrdens.map((os) => (
                  <tr key={os.id} className="hover:bg-slate-50/80 transition-colors">
                    <td className="py-3.5 px-4 font-black text-slate-900">
                      {os.id}
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      <div className="font-semibold text-slate-800">
                        {os.data_atendimento ? new Date(os.data_atendimento + 'T00:00:00').toLocaleDateString('pt-BR') : 'A agendar'}
                      </div>
                      <div className="text-[10px] text-slate-400">
                        {os.horario_inicio ? `${os.horario_inicio} às ${os.horario_final || 'fim'}` : 'Horário livre'}
                      </div>
                    </td>
                    <td className="py-3.5 px-4 max-w-[220px]">
                      <p className="font-medium text-slate-800 truncate" title={os.descricao}>
                        {os.descricao || 'Transporte de Carga'}
                      </p>
                      {os.quantidade_ajudante ? (
                        <p className="text-[10px] text-slate-400 font-medium">{os.quantidade_ajudante} ajudante(s)</p>
                      ) : null}
                    </td>
                    <td className="py-3.5 px-4 max-w-[260px]">
                      <div className="flex items-start space-x-1.5 text-slate-700">
                        <MapPin className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" />
                        <span className="truncate text-xs font-medium" title={os.retirada}>
                          {os.retirada || 'Retirada a combinar'}
                        </span>
                      </div>
                      <div className="flex items-start space-x-1.5 text-slate-500 mt-1">
                        <MapPin className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                        <span className="truncate text-[11px]" title={os.entrega}>
                          {os.entrega || 'Entrega a combinar'}
                        </span>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 whitespace-nowrap">
                      {getStatusBadge(os.status_id)}
                    </td>
                    <td className="py-3.5 px-4 text-center whitespace-nowrap">
                      <button
                        onClick={() => setSelectedOS(os)}
                        className="inline-flex items-center space-x-1 px-3 py-1.5 bg-slate-100 hover:bg-red-50 text-slate-700 hover:text-red-700 rounded-lg text-xs font-bold transition-colors"
                      >
                        <Eye className="w-3.5 h-3.5" />
                        <span>Detalhes</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Details */}
      {selectedOS && (
        <AreaClienteDetalhesOSModal
          os={selectedOS}
          onClose={() => {
            setSelectedOS(null);
            if (searchParams.get('os')) {
              searchParams.delete('os');
              setSearchParams(searchParams, { replace: true });
            }
          }}
          onAssinado={loadOS}
        />
      )}
    </div>
  );
}
